'use client';

import clsx from 'clsx';
import React, { ReactElement, useEffect, useMemo } from 'react';

import { Button } from '@/components/button/button';
import Modal from '@/components/modal/modal';
import { Heading } from '@/components/typography/heading';
import { MetricConfig, MetricConfigAPI, MetricConfigLLMScore } from '@/types/evals';

import styles from './modalMetricShow.module.scss';

type Props = {
  onCloseAction: () => void;
  metricConfig: MetricConfig;
};

const isAPIConfig = ( config: MetricConfigAPI | MetricConfigLLMScore ): config is MetricConfigAPI => {
  return 'url' in config;
};

const buildKeyValueFields = ( obj?: object, parentKey = '' ): ReactElement[] => {
  if( !obj ) return [];

  return Object.entries( obj ).flatMap( ( [ key, value ] ) => {
    const fullKey = parentKey ? `${ parentKey }.${ key }` : key;

    if ( typeof value === 'object' && value !== null && !Array.isArray( value ) ) {
      return buildKeyValueFields( value, fullKey );
    }

    let displayValue: string;
    if ( Array.isArray( value ) ) {
      displayValue = value.length > 0 ? value.join( ', ' ) : '-';
    } else {
      displayValue = value !== undefined && value !== null ? value.toString() : '-';
    }

    const testId = `metric-field-${ fullKey.replace( /\./g, '-' ) }`;

    return (
      <div className={ clsx( 'layout__col', styles.config_item ) } key={ `metric-key-${ fullKey }` } data-testid={ testId }>
        <fieldset>
          <legend className={ styles.key }>{ fullKey }</legend>
          <p className={ styles.val }>{ displayValue }</p>
        </fieldset>
      </div>
    );
  } );
};

export default function ModalMetricShow( { onCloseAction, metricConfig }: Props ) {
  // Close the modal on Escape press to match common UX expectations
  useEffect( () => {
    const onKeyDown = ( e: KeyboardEvent ) => {
      if ( e.key === 'Escape' ) {
        onCloseAction();
      }
    };

    document.addEventListener( 'keydown', onKeyDown );
    return () => document.removeEventListener( 'keydown', onKeyDown );
  }, [ onCloseAction ] );

  const { baseFields, configFields } = useMemo( () => {
    const configType = isAPIConfig( metricConfig.configuration ) ? 'API Classifier' : 'LLM as a Judge';

    const baseFields = {
      name: metricConfig.name,
      version: metricConfig.version,
      exampleType: metricConfig.exampleType,
      producesMetrics: metricConfig.producesMetrics,
      configType,
      createdAt: metricConfig.createdAt,
      createdBy: metricConfig.createdBy,
      isDeprecated: metricConfig.isDeprecated
    };

    const configFields = isAPIConfig( metricConfig.configuration )
      ? {
          url: metricConfig.configuration.url,
          isAsync: metricConfig.configuration.isAsync ?? false,
          headers: metricConfig.configuration.headers.map( h => `${ h.header }: ${ h.value }` )
        }
      : {
          prompt: metricConfig.configuration.prompt,
          model: metricConfig.configuration.modelParameters.model,
          temperature: metricConfig.configuration.modelParameters.temperature,
          promptParameters: metricConfig.configuration.promptParameters
        };

    return { baseFields, configFields };
  }, [ metricConfig ] );

  return (
    <Modal
      resizable
      modalType="metric-details"
      initialSize="large"
      minSize={ { width: 500, height: 300 } }
    >
      <div className={ styles.content } data-testid='metric-details-modal'>
        <Heading as="h2" size="md" data-testid='metric-name-title'>Metric: { metricConfig.name }</Heading>
        <div className={ clsx( 'layout__col', styles.doms ) } data-testid='metric-details-content'>
          { buildKeyValueFields( baseFields ) }
          <div className={ clsx( 'layout__col', styles.section_divider ) }>
            <Heading as="h3" size="sm">Configuration</Heading>
          </div>
          { buildKeyValueFields( configFields, 'configuration' ) }
        </div>
        <div className={ clsx( 'layout__row', 'pad', 'flex_right' ) }>
          <Button variant='primary' onClick={ onCloseAction } data-testid='metric-details-close-button'>Close</Button>
        </div>
      </div>
    </Modal>
  );
}
